import React, { useState, useEffect, useCallback } from 'react';
import { Button, Spinner, Alert } from 'react-bootstrap';
import { Eye, FileText, CheckCircle, Trash2, AlertTriangle, XCircle, Info, Percent } from 'lucide-react';
import { useParams } from 'react-router-dom';
import axios from '../../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../../utils/ApiUtils';
import { resolveRoleAccounts } from '../../../../../services/api/chartRolesApi';
import VATDetailView from '../VatForm';
import UnmappedAccountsPanel from '../UnmappedAccountsPanel';
import VatVerificationViewModal from '../VatVerificationViewModal';
import { sourceLabel } from '../sourceLabel';

// Severity now travels with the finding (Phase 3, G44/G45): several checks
// produce INFORMATION — "reverse charge without deduction is correct for your
// profile", "check the invoice date on EU acquisitions near the period
// boundary" — and painting those yellow trains users to dismiss the yellow
// ones that matter.
const VARIANT_BY_SEVERITY = { error: 'danger', warning: 'warning', info: 'light' };
const ICON_BY_SEVERITY = { error: XCircle, warning: AlertTriangle, info: Info };

function Finding({ finding }) {
    const severity = finding.severity || 'warning';
    const Icon = ICON_BY_SEVERITY[severity] || AlertTriangle;
    return (
        <>
            <style>{`
                /* This stylesheet paints .alert-warning as #FFAA2B text on a pale orange
                   ground — yellow on yellow, reported as unreadable. Same family as the
                   .alert-success neon fixed earlier. Amber stays the signal colour, but on a
                   readable slate body. */
                .vat-advisory .alert-warning,
                .vat-advisory.alert-warning {
                  color: #78350f;
                  background: #fffbeb;
                  border-color: #fcd34d;
                }
                .vat-advisory .alert-light,
                .vat-advisory.alert-light {
                  color: #475569;
                  background: #f8fafc;
                  border-color: #e2e8f0;
                }
            `}</style>
        <Alert variant={VARIANT_BY_SEVERITY[severity] || 'warning'} className="vat-advisory d-flex align-items-start">

            <Icon size={18} className="me-2 mt-1 flex-shrink-0" />
            <div>
                {finding.message}
                {finding.sourceUrl && (
                    <>
                        {' '}
                        <a href={finding.sourceUrl} target="_blank" rel="noopener noreferrer">
                            {sourceLabel(finding.sourceUrl)}
                        </a>
                    </>
                )}
            </div>
        </Alert>
        </>
    );
}

const ReviewStep = ({
    vatPeriod,
    handleReviewVatPeriod,
    reviewVerification,
    handleDeleteVerification,
    operationInProgress,
    isPreviewMode
}) => {
    // Mirrors the server's elapsed test: a period is still running until its end
    // date has passed. Kept in one expression so the banner and the header cannot
    // disagree about it.
    const periodStillRunning = Boolean(vatPeriod?.end_date)
        && new Date(new Date().toDateString()) <= new Date(`${String(vatPeriod.end_date).slice(0, 10)}T00:00:00`);

    const { companyId } = useParams();
    const [validation, setValidation] = useState(null);
    // Step 1's pre-flight already raises these, and showing them again here put
    // the same paragraph in front of the user twice in one pass — the same
    // duplication as the open-earlier-period warning. Step 1 is where the user
    // still has room to act; granska is for the figures.
    const STEP_ONE_WARNINGS = new Set([
        'cash_method_year_end_pull_in', 'cash_method_year_end_clear', 'pro_rata_year_end_truing',
    ]);
    // S6 — step 2 shows at most a compact note when step 1's year-end check
    // found unbooked items (tier 1) or could only remind (tier 2); nothing
    // when tier 1 confirmed a clean year end.
    const yearEndNote = (validation?.warnings || []).find((w) => w.type === 'cash_method_year_end_pull_in');
    const reviewWarnings = (validation?.warnings || [])
        .filter((warning) => !STEP_ONE_WARNINGS.has(warning.type));
    const [mappingValidation, setMappingValidation] = useState(null);
    // Bumped when an unmapped account is resolved from this step, so the
    // blankett below refetches its (now different) figures.
    const [formReloadKey, setFormReloadKey] = useState(0);
    const [showVerificationModal, setShowVerificationModal] = useState(false);
    // R-BOX-3 — a registered business with nothing to report must STILL file,
    // with 0 kr in ruta 49. The wizard used to assume activity, so a mandatory
    // nolldeklaration looked exactly like a period that could be skipped (G11).
    const [nilReturn, setNilReturn] = useState(null);
    const [declaringNil, setDeclaringNil] = useState(false);
    // Blandad verksamhet (G9): what the profile says the period's field-48
    // reduction should be, and whether it has been recorded.
    const [mixedDeduction, setMixedDeduction] = useState(null);
    const [applyingDeduction, setApplyingDeduction] = useState(false);
    // Resolved, not hardcoded — see the note in RecordStep (G37/G47).
    const [settlementAccount, setSettlementAccount] = useState(null);

    useEffect(() => {
        let cancelled = false;
        resolveRoleAccounts(companyId, ['vat_settlement'])
            .then((accounts) => {
                if (!cancelled) setSettlementAccount(accounts?.vat_settlement?.accountNumber || null);
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [companyId]);

    // Re-fetch validation when vatPeriod changes OR when verification is created/deleted
    // The zeroing verification moves amounts off VAT accounts, changing the box values
    const fetchValidation = useCallback(() => {
        if (!vatPeriod) return;
        axios.get(getApiUrl(`${companyId}/reporting/vat-validation`), {
            params: { start_date: vatPeriod.start_date, end_date: vatPeriod.end_date, fiscal_year_id: vatPeriod.fiscal_year_id },
            withCredentials: true
        }).then(res => {
            if (res.data.success) setValidation(res.data.validation);
        }).catch(err => console.error('Validation fetch error:', err));

        axios.get(getApiUrl(`${companyId}/reporting/vat-mapping-validation`), {
            withCredentials: true
        }).then(res => {
            if (res.data.success) setMappingValidation(res.data.validation);
        }).catch(err => console.error('Mapping validation fetch error:', err));

        axios.get(
            getApiUrl(`${companyId}/reporting/vat-period/${vatPeriod.vat_reporting_period_id}/nil-return`),
            { withCredentials: true }
        ).then(res => {
            if (res.data.success) {
                setNilReturn(res.data.status);
                // The same endpoint's `warnings` (an earlier period still open)
                // are rendered on step 1 only. Showing them here as well put the
                // identical warning twice in one pass through the wizard; G25
                // requires it said, not said twice, and step 1 is where the user
                // still has room to act on it (owner, 2026-09-02).
            }
        }).catch(err => console.error('Nil-return status fetch error:', err));

        axios.get(
            getApiUrl(`${companyId}/reporting/vat-period/${vatPeriod.vat_reporting_period_id}/mixed-deduction`),
            { withCredentials: true }
        ).then(res => {
            if (res.data.success) setMixedDeduction(res.data);
        }).catch(err => console.error('Mixed-deduction fetch error:', err));
    }, [companyId, vatPeriod]);

    const applyMixedDeduction = async () => {
        setApplyingDeduction(true);
        try {
            const response = await axios.post(
                getApiUrl(`${companyId}/reporting/vat-period/${vatPeriod.vat_reporting_period_id}/mixed-deduction`),
                {},
                { withCredentials: true }
            );
            if (response.data.success) fetchValidation();
        } catch (error) {
            console.error('Error applying mixed deduction:', error);
        } finally {
            setApplyingDeduction(false);
        }
    };

    const declareNilReturn = async () => {
        setDeclaringNil(true);
        try {
            const response = await axios.post(
                getApiUrl(`${companyId}/reporting/vat-period/${vatPeriod.vat_reporting_period_id}/nil-return`),
                {},
                { withCredentials: true }
            );
            if (response.data.success) {
                setNilReturn(prev => (prev ? { ...prev, nil_return_declared: true } : prev));
                fetchValidation();
            }
        } catch (error) {
            console.error('Error declaring nil return:', error);
        } finally {
            setDeclaringNil(false);
        }
    };

    useEffect(() => {
        fetchValidation();
    }, [fetchValidation, reviewVerification]);

    return (
        <div className="step-container">
            <style>{`
                .verification-status-card {
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 1rem;
                    margin-top: 1rem;
                }
                .verification-status-card .btn-sm {
                    padding: 0.375rem 0.75rem;
                    font-size: 0.875rem;
                }
            `}</style>

            <div className="step-header">
                <div className="d-flex align-items-center">
                    <div
                        className="rounded-circle d-flex align-items-center justify-content-center me-3"
                        style={{
                            width: '48px',
                            height: '48px',
                            backgroundColor: 'var(--primary)',
                            color: 'white'
                        }}
                    >
                        <Eye size={24} />
                    </div>
                    <div>
                        <h3 className="mb-1">Granska Momsrapporten</h3>
                        {/* The step is reachable while the period is still running
                            (owner ruling, 2026-09-04). Useful to look at, but the
                            figures will still move, so the screen says so rather
                            than presenting a running period's numbers as final.
                            Generating and freezing remain blocked server-side. */}
                        <p className="text-muted mb-0">
                            {periodStillRunning
                                ? `Preliminär — perioden pågår till ${vatPeriod.end_date}. `
                                  + 'Siffrorna kan ändras och rapporten kan inte låsas än.'
                                : 'Kontrollera att uppgifterna är korrekta innan du fortsätter'}
                        </p>
                    </div>
                </div>
            </div>

            <div className="step-content">
                {/* Only show validation warnings BEFORE the verification is created */}
                {/* After booking, the VAT accounts are zeroed so the warnings become misleading */}
                {!reviewVerification && (
                    <>
                        {/* Koppla konton (spec §4, B3) — the same panel as step 1, so a
                            user who lands here with unresolved accounts sees WHY locking
                            will refuse instead of meeting a bare 409. Resolving an account
                            recomputes the figures below on the spot. The panel renders
                            nothing when there is nothing to resolve. */}
                        <UnmappedAccountsPanel
                            companyId={companyId}
                            vatPeriodId={vatPeriod?.vat_reporting_period_id}
                            onResolved={() => { fetchValidation(); setFormReloadKey((k) => k + 1); }}
                        />

                        {/* VAT Mapping Setup Warnings */}
                        {mappingValidation && (mappingValidation.errors.length > 0 || mappingValidation.warnings.length > 0) && (
                            <div className="mb-3">
                                {mappingValidation.errors.map((err, i) => (
                                    <Finding key={`me-${i}`} finding={{ ...err, severity: 'error' }} />
                                ))}
                                {mappingValidation.warnings.map((warn, i) => (
                                    <Finding key={`mw-${i}`} finding={warn} />
                                ))}
                            </div>
                        )}

                        {yearEndNote && (
                            <div className="small text-muted mb-3" data-testid="year-end-note">
                                Årsskiftesregeln (ML 7 kap. 16 §) gäller den här perioden — se
                                kontrollen under Instruktioner innan du låser.
                            </div>
                        )}

                        {/* VAT Report Validation */}
                        {validation && (validation.errors.length > 0 || reviewWarnings.length > 0) && (
                            <div className="mb-3">
                                <h6>Momskontroll</h6>
                                {validation.errors.map((err, i) => (
                                    <Finding key={`ve-${i}`} finding={{ ...err, severity: 'error' }} />
                                ))}
                                {reviewWarnings.map((warn, i) => (
                                    <Finding key={`vw-${i}`} finding={warn} />
                                ))}
                    </div>
                )}
                    </>
                )}

                {/* Blandad verksamhet (G9). Shown only when the räkenskapsår has a
                    pro-rata profile — for everyone else there is nothing to say,
                    and an empty panel about mixed activity is noise. */}
                {!reviewVerification && mixedDeduction?.proposal?.applies && (
                    <Alert
                        variant={mixedDeduction.recorded ? 'light' : 'warning'}
                        className="d-flex align-items-start mb-3"
                    >
                        <Percent size={18} className="me-2 mt-1 flex-shrink-0" />
                        <div className="flex-grow-1">
                            <strong>Blandad verksamhet — {mixedDeduction.proposal.deductible_percent} % avdragsgill andel</strong>
                            <div>
                                Ingående moms i perioden är {Math.round(mixedDeduction.proposal.gross_input_vat)} kr.
                                {' '}Med profilen dras {Math.round(mixedDeduction.proposal.deductible_input_vat)} kr av i
                                fält 48, en minskning med {Math.abs(mixedDeduction.proposal.adjustment)} kr.
                            </div>
                            {mixedDeduction.recorded ? (
                                <div className="text-muted mt-1">
                                    Fördelningen är bokförd på perioden
                                    {mixedDeduction.recorded.resolved_at ? ` (${mixedDeduction.recorded.resolved_at})` : ''}.
                                    {Math.abs(Number(mixedDeduction.recorded.amount)) !== Math.abs(mixedDeduction.proposal.adjustment)
                                        && ' Underlaget har ändrats sedan dess — räkna om innan du går vidare.'}
                                </div>
                            ) : (
                                <div className="text-muted mt-1">
                                    Fördelningen är ännu inte tillämpad på perioden, så fält 48 visar hela
                                    den ingående momsen.
                                </div>
                            )}
                            <Button
                                size="sm"
                                variant="outline-primary"
                                className="mt-2"
                                onClick={applyMixedDeduction}
                                disabled={applyingDeduction || isPreviewMode}
                            >
                                {applyingDeduction ? 'Räknar om…' : 'Tillämpa fördelningen på perioden'}
                            </Button>
                        </div>
                    </Alert>
                )}

                {vatPeriod ? (
                    <div className="mb-4">
                        <VATDetailView
                            period={vatPeriod}
                            fiscalYearId={vatPeriod.fiscal_year_id}
                            reloadKey={formReloadKey}
                        />
                    </div>
                ) : (
                    <div className="d-flex align-items-center py-4">
                        <Spinner animation="border" size="sm" className="me-2" />
                        <span>Laddar momsuppgifter...</span>
                    </div>
                )}
            </div>

            <div className="step-actions">
                {reviewVerification ? (
                    <div className="d-flex align-items-center justify-content-between">
                        <button
                            type="button"
                            onClick={() => setShowVerificationModal(true)}
                            className="d-inline-flex align-items-center gap-2 border-0 bg-transparent p-0"
                            style={{ fontSize: '0.85rem', cursor: 'pointer' }}
                        >
                            <CheckCircle size={16} className="text-success" />
                            <span style={{ color: 'var(--primary-dark)', fontWeight: 500, textDecoration: 'underline', textUnderlineOffset: '2px' }}>
                                Verifikat {reviewVerification.series}{reviewVerification.number}
                            </span>
                        </button>
                        {vatPeriod?.state === 'open' && (
                            <Button
                                variant="link"
                                size="sm"
                                className="text-danger p-0"
                                onClick={() => handleDeleteVerification(reviewVerification.verification_id, 'review')}
                                disabled={operationInProgress || isPreviewMode}
                                style={{ fontSize: '0.8rem' }}
                            >
                                <Trash2 size={13} className="me-1" />
                                Ångra
                            </Button>
                        )}
                    </div>
                ) : nilReturn?.is_empty ? (
                    /* An empty period: filing zero is mandatory, not optional,
                       and there is no settlement verifikat to create because
                       nothing moved. */
                    <>
                        <Alert variant="info" className="mb-3">
                            <div className="vat-advisory d-flex align-items-start">
                                <AlertTriangle size={18} className="me-2 mt-1 flex-shrink-0" />
                                <div>
                                    <strong>Ingen moms att redovisa för perioden</strong>
                                    <div className="small mt-1">{nilReturn.message}</div>
                                    <div className="small mt-1 text-muted">
                                        Skatteverket, <em>Fylla i momsdeklarationen</em>.{' '}
                                        <a href={nilReturn.source_url} target="_blank" rel="noopener noreferrer">
                                            Läs mer
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </Alert>
                        <div className="d-flex justify-content-center">
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={declareNilReturn}
                                className="d-flex align-items-center px-4"
                                disabled={operationInProgress || isPreviewMode || declaringNil
                                    || nilReturn.nil_return_declared}
                            >
                                {declaringNil
                                    ? <Spinner animation="border" size="sm" className="me-2" />
                                    : <CheckCircle size={20} className="me-2" />}
                                {nilReturn.nil_return_declared
                                    ? 'Nolldeklaration markerad'
                                    : 'Deklarera noll'}
                            </Button>
                        </div>
                        <div className="text-center mt-3 text-muted small">
                            Momsrapporten genereras med 0 kr i fält 49. Inget verifikat behövs —
                            ingenting har flyttats.
                        </div>
                    </>
                ) : (
                    <>
                        {/* Period-end bookkeeping: the verifikat zeroes the VAT
                            accounts into the settlement account, so booking it
                            mid-period would clear balances that are still moving.
                            Same guard family as close and freeze, and the server
                            refuses it with 409 regardless of this button (owner
                            ruling, 2026-09-04). Not offered rather than offered
                            and refused. */}
                        {periodStillRunning ? (
                            <div className="text-center text-muted small">
                                Bokföringsförslaget kan skapas när perioden är slut
                                ({vatPeriod.end_date}). Momskontona nollställs först då, så att
                                de inte töms mitt i en pågående period.
                            </div>
                        ) : (
                            <>
                                <div className="d-flex justify-content-center">
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={handleReviewVatPeriod}
                                        className="d-flex align-items-center px-4"
                                        disabled={operationInProgress || isPreviewMode}
                                    >
                                        <FileText size={20} className="me-2" />
                                        Skapa bokföringsförslag för momsrapport
                                    </Button>
                                </div>
                                <div className="text-center mt-3 text-muted small">
                                    Momskontona nollställs och saldot flyttas till redovisningskontot{settlementAccount ? ` (${settlementAccount})` : ''}.
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>


            {reviewVerification && (
                <VatVerificationViewModal
                    show={showVerificationModal}
                    onHide={() => setShowVerificationModal(false)}
                    verificationId={reviewVerification.verification_id}
                />
            )}
        </div>
    );
};

export default ReviewStep;